import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../services/api";
import { format } from "date-fns";
import ReactPaginate from "react-paginate";

const woStatusBadge = (status) => {
  const map = {
    "Pending":                 "ims-badge-pending",
    "Approved by Principal":   "ims-badge-principal",
    "Approved by Management":  "ims-badge-approved",
    "Partially Completed":     "ims-badge-partial",
    "Completed":               "ims-badge-full",
    "Rejected by Principal":   "ims-badge-rejected",
    "Rejected by Management":  "ims-badge-rejected",
  };
  return <span className={`ims-badge ${map[status] || "ims-badge-notcreated"}`}>{status}</span>;
};

const WorkOrderList = () => {
  const [orders, setOrders]         = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const navigate = useNavigate();
  const user  = JSON.parse(localStorage.getItem("user"));
  const token = localStorage.getItem("token");

  const fetchOrders = async () => {
    try {
      const res = await axios.get("/work-orders", {
        params: { page: currentPage + 1, limit: itemsPerPage, search: searchTerm, role: user.role },
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(res.data.orders || []);
      setTotalPages(res.data.totalPages || 0);
    } catch (err) {
      console.error("Error fetching work orders:", err);
    }
  };

  useEffect(() => { fetchOrders(); }, [currentPage, searchTerm, itemsPerPage]);

  const generatePDF = async (id, wo_number) => {
    try {
      const res = await axios.get(`/work-orders/${id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (err) {
      console.error("Error generating WO PDF:", err);
      alert("Failed to generate PDF");
    }
  };

  return (
    <div>
      {/* ── Page header ── */}
      <div className="ims-page-header">
        <div>
          <h4>Work Orders</h4>
          <p>Manage service work orders for contractors</p>
        </div>
        {user.role === "store_manager" && (
          <button className="btn btn-sm btn-primary" onClick={() => navigate("/dashboard/create-work-order")}>
            <i className="fas fa-plus mr-1"></i> New Work Order
          </button>
        )}
      </div>

      {/* ── Controls ── */}
      <div className="row mb-3 align-items-center">
        <div className="col-md-4 d-flex align-items-center">
          <label className="mr-2 mb-0" style={{ whiteSpace: "nowrap", fontSize: "13px" }}>Show:</label>
          <select
            className="form-control form-control-sm w-auto"
            value={itemsPerPage}
            onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(0); }}
          >
            {[10, 20, 50, 100].map(n => <option key={n}>{n}</option>)}
          </select>
        </div>
        <div className="col-md-4">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search work orders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* ── Table ── */}
      <div className="table-responsive">
        <table className="ims-table">
          <thead>
            <tr>
              <th>Sr</th>
              <th>WO Date</th>
              <th>WO No.</th>
              <th>Subject</th>
              <th>Contractor</th>
              <th>Total (₹)</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.length > 0 ? orders.map((wo, i) => (
              <tr key={wo.id}>
                <td>{i + 1 + currentPage * itemsPerPage}</td>
                <td>{format(new Date(wo.wo_date), "dd-MM-yyyy")}</td>
                <td><strong>{wo.wo_number}</strong></td>
                <td style={{ maxWidth: "200px" }}>{wo.title}</td>
                <td>{wo.vendor_name}</td>
                <td style={{ fontWeight: 600 }}>₹{Number(wo.grand_total).toLocaleString("en-IN")}</td>
                <td>{woStatusBadge(wo.status)}</td>
                <td>
                  {/* View */}
                  <button
                    className="ims-btn ims-btn-view"
                    title="View"
                    onClick={() => navigate(`/dashboard/view-work-order/${wo.id}`)}
                  >
                    <i className="fas fa-eye"></i>
                  </button>

                  {/* Edit — store_manager + Pending */}
                  {user.role === "store_manager" && wo.status === "Pending" && (
                    <button
                      className="ims-btn ims-btn-edit"
                      title="Edit"
                      onClick={() => navigate(`/dashboard/edit-work-order/${wo.id}`)}
                    >
                      <i className="fas fa-pen"></i>
                    </button>
                  )}

                  {/* Print PDF — Approved by Management or beyond */}
                  {["Approved by Management", "Partially Completed", "Completed"].includes(wo.status) && (
                    <button
                      className="ims-btn ims-btn-pdf"
                      title="Print PDF"
                      onClick={() => generatePDF(wo.id, wo.wo_number)}
                    >
                      <i className="fas fa-print"></i>
                    </button>
                  )}

                  {/* Mark Completion — store_manager + after management approval */}
                  {user.role === "store_manager" &&
                    ["Approved by Management", "Partially Completed"].includes(wo.status) && (
                    <button
                      className="ims-btn ims-btn-grn"
                      title="Mark Completion"
                      onClick={() => navigate(`/dashboard/view-work-order/${wo.id}`)}
                    >
                      <i className="fas fa-check-circle"></i>
                    </button>
                  )}
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan="8" className="text-center text-muted py-4">No work orders found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      <ReactPaginate
        previousLabel={"← Previous"} nextLabel={"Next →"} breakLabel={"..."}
        pageCount={totalPages} marginPagesDisplayed={2} pageRangeDisplayed={5}
        onPageChange={({ selected }) => setCurrentPage(selected)}
        containerClassName={"pagination justify-content-center mt-3"}
        pageClassName={"page-item"} pageLinkClassName={"page-link"}
        previousClassName={"page-item"} previousLinkClassName={"page-link"}
        nextClassName={"page-item"} nextLinkClassName={"page-link"}
        breakClassName={"page-item"} breakLinkClassName={"page-link"}
        activeClassName={"active"}
      />
    </div>
  );
};

export default WorkOrderList;
